import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Calendar, Loader2, ArrowLeft } from 'lucide-react';
import { trackChatbotOpen, trackChatbotMessage } from '../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CALENDLY_LINK = "https://calendly.com/agenticatoms/30min";

// Simple markdown parser for bold text and bullet points
const formatMessage = (text) => {
  if (!text) return null;
  
  // Split by newlines to handle line breaks
  const lines = text.split('\n');
  
  return lines.map((line, lineIndex) => {
    // Parse **bold** text
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    
    const formattedLine = parts.map((part, partIndex) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        // Bold text - remove ** and wrap in strong
        return <strong key={partIndex} className="font-semibold">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
    
    return (
      <span key={lineIndex}>
        {formattedLine}
        {lineIndex < lines.length - 1 && <br />}
      </span>
    );
  });
};

// Animated Atom Icon - Matches "Agentic Atoms" branding
const AtomIcon = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 50 50" fill="none">
    {/* Center nucleus - pulsing */}
    <motion.circle
      cx="25"
      cy="25"
      r="6"
      fill="white"
      animate={{ 
        scale: [1, 1.2, 1],
        opacity: [1, 0.8, 1]
      }}
      transition={{ duration: 1.5, repeat: Infinity }}
    />
    
    {/* Orbit 1 - horizontal */}
    <motion.ellipse
      cx="25"
      cy="25"
      rx="20"
      ry="8"
      stroke="white"
      strokeWidth="1.5"
      fill="none"
      animate={{ rotate: 360 }}
      transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
      style={{ transformOrigin: "center" }}
    />
    
    {/* Electron 1 */}
    <motion.circle
      cx="45"
      cy="25"
      r="3"
      fill="#4ADE80"
      initial={{ cx: 45, cy: 25 }}
      animate={{ 
        cx: [45, 25, 5, 25, 45],
        cy: [25, 17, 25, 33, 25]
      }}
      transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
    />
    
    {/* Orbit 2 - tilted left */}
    <motion.ellipse
      cx="25"
      cy="25"
      rx="20"
      ry="8"
      stroke="white"
      strokeWidth="1.5"
      fill="none"
      style={{ transformOrigin: "center" }}
      animate={{ rotate: 360 }}
      transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
      transform="rotate(60 25 25)"
    />
    
    {/* Electron 2 */}
    <motion.circle
      cx={25 + 20*Math.cos(Math.PI/3)}
      cy={25 + 8*Math.sin(Math.PI/3)}
      r="3"
      fill="#60A5FA"
      initial={{ cx: 25 + 20*Math.cos(Math.PI/3), cy: 25 + 8*Math.sin(Math.PI/3) }}
      animate={{ 
        cx: [25 + 20*Math.cos(Math.PI/3), 25 + 20*Math.cos(Math.PI/3 + Math.PI/2), 25 + 20*Math.cos(Math.PI/3 + Math.PI), 25 + 20*Math.cos(Math.PI/3 + 3*Math.PI/2), 25 + 20*Math.cos(Math.PI/3)],
        cy: [25 + 8*Math.sin(Math.PI/3), 25 - 8*Math.sin(Math.PI/3 + Math.PI/2), 25 + 8*Math.sin(Math.PI/3 + Math.PI), 25 - 8*Math.sin(Math.PI/3 + 3*Math.PI/2), 25 + 8*Math.sin(Math.PI/3)]
      }}
      transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
    />
    
    {/* Orbit 3 - tilted right */}
    <motion.ellipse
      cx="25"
      cy="25"
      rx="20"
      ry="8"
      stroke="white"
      strokeWidth="1.5"
      fill="none"
      style={{ transformOrigin: "center" }}
      animate={{ rotate: 360 }}
      transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
      transform="rotate(-60 25 25)"
    />
    
    {/* Electron 3 */}
    <motion.circle
      cx={25 - 20*Math.cos(Math.PI/3)}
      cy={25 + 8*Math.sin(Math.PI/3)}
      r="3"
      fill="#F472B6"
      initial={{ cx: 25 - 20*Math.cos(Math.PI/3), cy: 25 + 8*Math.sin(Math.PI/3) }}
      animate={{ 
        cx: [25 - 20*Math.cos(Math.PI/3), 25 - 20*Math.cos(Math.PI/3 + Math.PI/2), 25 - 20*Math.cos(Math.PI/3 + Math.PI), 25 - 20*Math.cos(Math.PI/3 + 3*Math.PI/2), 25 - 20*Math.cos(Math.PI/3)],
        cy: [25 + 8*Math.sin(Math.PI/3), 25 - 8*Math.sin(Math.PI/3 + Math.PI/2), 25 + 8*Math.sin(Math.PI/3 + Math.PI), 25 - 8*Math.sin(Math.PI/3 + 3*Math.PI/2), 25 + 8*Math.sin(Math.PI/3)]
      }}
      transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
    />
  </svg>
);

// Small atom for header
const SmallAtomIcon = () => (
  <svg width="28" height="28" viewBox="0 0 50 50" fill="none">
    <circle cx="25" cy="25" r="5" fill="white" />
    <ellipse cx="25" cy="25" rx="18" ry="7" stroke="white" strokeWidth="2" fill="none" />
    <ellipse cx="25" cy="25" rx="18" ry="7" stroke="white" strokeWidth="2" fill="none" transform="rotate(60 25 25)" />
    <ellipse cx="25" cy="25" rx="18" ry="7" stroke="white" strokeWidth="2" fill="none" transform="rotate(-60 25 25)" />
  </svg>
);

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadData, setLeadData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    purpose: ''
  });
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  
  const messagesEndRef = useRef(null);

  // Rotating welcome messages
  const welcomeMessages = [
    "Hey there! Ready to transform your business with AI? What can I help you with today?",
    "Welcome! I'm your AI assistant. Whether it's automation, websites, or chatbots — I've got you covered.",
    "Hi! Looking to supercharge your business? Let's talk about how AI can help you grow.",
    "Hello! From custom AI apps to workflow automation — tell me what you're looking to build.",
    "Hey! Every great project starts with a conversation. What brings you here today?",
    "Welcome aboard! I help businesses like yours leverage AI. What's on your mind?",
    "Hi there! Whether you need a website, chatbot, or full automation — I'm here to help.",
    "Hello! Ready to explore what AI can do for your business? Ask me anything!"
  ];

  // Initialize with a random welcome message when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const randomMessage = welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)];
      setMessages([{
        id: '1',
        type: 'bot',
        text: randomMessage,
        timestamp: new Date()
      }]);
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    trackChatbotMessage();

    const userMessage = {
      id: Date.now().toString(),
      type: 'user',
      text: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: inputValue,
          session_id: sessionId
        })
      });

      const data = await response.json();

      const botMessage = {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        text: data.response,
        timestamp: new Date(),
        hasSchedulingIntent: data.has_scheduling_intent
      };

      setMessages(prev => [...prev, botMessage]);
      
      if (data.has_scheduling_intent) {
        setTimeout(() => setShowLeadForm(true), 1000);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        type: 'bot',
        text: "Connection issue. Please try again.",
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleLeadInputChange = (e) => {
    const { name, value } = e.target;
    setLeadData(prev => ({ ...prev, [name]: value }));
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadData.firstName || !leadData.lastName || !leadData.email || !leadData.phone || !leadData.purpose) return;
    
    setIsSubmittingLead(true);
    
    try {
      const response = await fetch(`${API_URL}/api/leads/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: leadData.firstName,
          last_name: leadData.lastName,
          email: leadData.email,
          phone: leadData.phone,
          purpose: leadData.purpose,
          source: 'chatbot'
        })
      });
      
      if (response.ok) {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          type: 'bot',
          text: `Thanks ${leadData.firstName}! Click below to book your appointment.`,
          timestamp: new Date(),
          showCalendlyButton: true
        }]);
      } else {
        throw new Error('Failed to save lead');
      }
      
      setShowLeadForm(false);
      setLeadData({ firstName: '', lastName: '', email: '', phone: '', purpose: '' });
    } catch (error) {
      console.error('Lead submission error:', error);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'bot',
        text: `Thanks ${leadData.firstName}! Click below to book your appointment.`,
        timestamp: new Date(),
        showCalendlyButton: true
      }]);
      setShowLeadForm(false);
      setLeadData({ firstName: '', lastName: '', email: '', phone: '', purpose: '' });
    } finally {
      setIsSubmittingLead(false);
    }
  };

  return (
    <>
      {/* Chat Toggle Button - Animated Atom Icon */}
      {!isOpen && (
        <motion.button
          onClick={() => { setIsOpen(true); trackChatbotOpen(); }}
          className="fixed bottom-6 right-6 z-[9999] w-16 h-16 bg-[#FF6B00] rounded-full shadow-xl shadow-orange-500/40 flex items-center justify-center hover:shadow-2xl hover:shadow-orange-500/60 transition-all duration-300"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.95 }}
          data-testid="chat-widget-toggle"
        >
          <AtomIcon size={44} />
        </motion.button>
      )}

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 z-[9999] w-[360px] h-[500px] bg-[#0a0a0a] rounded-2xl shadow-2xl shadow-orange-500/20 flex flex-col overflow-hidden border border-[#FF6B00]/30"
            data-testid="chat-widget-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#FF6B00] to-[#FF8C3B] text-white p-3 flex items-center justify-between border-b border-orange-300/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/15 backdrop-blur-sm rounded-full flex items-center justify-center ring-1 ring-white/30">
                  <SmallAtomIcon />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Agentic Atoms AI</h3>
                  <div className="flex items-center gap-1.5">
                    <motion.span 
                      className="w-2 h-2 bg-green-300 rounded-full shadow-[0_0_6px_rgba(74,222,128,0.8)]"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    <p className="text-xs text-white/90">Online</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 bg-white/15 hover:bg-white/25 rounded-lg transition-colors"
                data-testid="chat-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Lead Capture Form */}
            <AnimatePresence>
              {showLeadForm && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="absolute inset-0 bg-[#0a0a0a] z-10 flex flex-col"
                >
                  <div className="bg-gradient-to-r from-[#FF6B00] to-[#FF8C3B] text-white p-3 flex items-center gap-2">
                    <button onClick={() => setShowLeadForm(false)} className="p-1.5 hover:bg-white/20 rounded-lg">
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                      <h3 className="font-semibold text-sm">Book Your Consultation</h3>
                      <p className="text-xs text-white/85">Step 1 of 2: Your details</p>
                    </div>
                  </div>
                  
                  <form onSubmit={handleLeadSubmit} className="flex-1 p-4 overflow-y-auto">
                    <p className="text-xs text-gray-400 mb-4 bg-white/5 border border-white/10 p-2 rounded-lg">
                      Fill in your details below, then you'll be directed to our calendar to pick a time.
                    </p>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">First Name *</label>
                          <input
                            type="text"
                            name="firstName"
                            value={leadData.firstName}
                            onChange={handleLeadInputChange}
                            required
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00]"
                            placeholder="John"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">Last Name *</label>
                          <input
                            type="text"
                            name="lastName"
                            value={leadData.lastName}
                            onChange={handleLeadInputChange}
                            required
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00]"
                            placeholder="Doe"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-300 mb-1">Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={leadData.email}
                          onChange={handleLeadInputChange}
                          required
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00]"
                          placeholder="john@company.com"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-300 mb-1">Phone *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={leadData.phone}
                          onChange={handleLeadInputChange}
                          required
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00]"
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-300 mb-1">Purpose *</label>
                        <textarea
                          name="purpose"
                          value={leadData.purpose}
                          onChange={handleLeadInputChange}
                          required
                          rows={2}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00] resize-none"
                          placeholder="Brief description..."
                        />
                      </div>
                      
                      <button
                        type="submit"
                        disabled={isSubmittingLead}
                        className="w-full py-2.5 bg-[#FF6B00] text-white rounded-lg font-medium hover:bg-[#FF8C3B] shadow-lg shadow-orange-500/30 disabled:opacity-50 flex items-center justify-center gap-2 text-sm transition-all"
                      >
                        {isSubmittingLead ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Calendar className="w-4 h-4" /> Continue</>}
                      </button>
                    </div>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#0a0a0a]">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] p-3 rounded-2xl text-sm ${
                      msg.type === 'user'
                        ? 'bg-[#FF6B00] text-white rounded-br-md shadow-lg shadow-orange-500/20'
                        : 'bg-white/[0.06] text-gray-100 rounded-bl-md border border-white/10 backdrop-blur-sm'
                    }`}
                  >
                    <div className="whitespace-pre-wrap">{formatMessage(msg.text)}</div>
                    
                    {msg.hasSchedulingIntent && !showLeadForm && (
                      <button
                        onClick={() => setShowLeadForm(true)}
                        className="mt-3 flex items-center gap-2 px-4 py-2 bg-[#FF6B00] text-white rounded-lg text-xs hover:bg-[#FF8C3B] w-full justify-center font-medium shadow-md shadow-orange-500/30 transition-all"
                      >
                        <Calendar className="w-4 h-4" /> Book Appointment Now
                      </button>
                    )}
                    
                    {msg.showCalendlyButton && (
                      <a
                        href={CALENDLY_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 flex items-center gap-2 px-4 py-2 bg-[#FF6B00] text-white rounded-lg text-xs hover:bg-[#FF8C3B] w-full justify-center font-medium shadow-md shadow-orange-500/30 transition-all"
                      >
                        <Calendar className="w-4 h-4" /> Open Calendly
                      </a>
                    )}
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white/[0.06] border border-white/10 p-3 rounded-2xl rounded-bl-md backdrop-blur-sm">
                    <div className="flex gap-1.5">
                      <motion.span 
                        className="w-2 h-2 bg-[#FF6B00] rounded-full"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                      />
                      <motion.span 
                        className="w-2 h-2 bg-[#FF6B00] rounded-full"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0.15 }}
                      />
                      <motion.span 
                        className="w-2 h-2 bg-[#FF6B00] rounded-full"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0.3 }}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-white/10 bg-[#0a0a0a]">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me anything..."
                  className="flex-1 px-4 py-2.5 bg-white/[0.06] border border-white/10 text-white placeholder-gray-500 rounded-full text-sm focus:outline-none focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00]"
                  disabled={isLoading}
                  data-testid="chat-input"
                />
                <motion.button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isLoading}
                  className="w-10 h-10 bg-[#FF6B00] rounded-full flex items-center justify-center hover:bg-[#FF8C3B] shadow-lg shadow-orange-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  data-testid="chat-send-btn"
                >
                  <Send className="w-4 h-4 text-white" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
